// src/components/character/CharacterEditModal.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getCharacter, updateCharacter, updateCharacterAvatar } from '../../services/characterService';
import { Character } from '../../types/character';
import { PhotoIcon, CurrencyYenIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import XorPixViewer from '../xorpix/XorPixViewer';
import ARCreateSection from './ARCreateSection';
import LicensingSettingsPanel from './LicensingSettingsPanel';
import { updateSaleSettings, validateSalePrice, formatPrice } from '../../services/adoptionService';
import { updateCharacter as updateCharacterService } from '../../services/characterService';
import { CHARACTER_TAGS, getTagCategory } from '../../constants/characterTags';
import { showToast } from '../common/Toast';
import { CharacterLicensing } from '../../types/character';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { config } from '../../config';

interface CharacterEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  characterId: string | null;
  onSaved?: () => void;
}

const CharacterEditModal: React.FC<CharacterEditModalProps> = ({ isOpen, onClose, characterId, onSaved }) => {
  const { t } = useTranslation('character');
  const { t: tTop } = useTranslation('top');
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [character, setCharacter] = useState<Character | null>(null);

  // Sale settings state
  const [forSale, setForSale] = useState(false);
  const [salePrice, setSalePrice] = useState<number>(5000);
  const [isSavingSale, setIsSavingSale] = useState(false);
  const [saleSaveSuccess, setSaleSaveSuccess] = useState(false);
  const [salePriceError, setSalePriceError] = useState<string | null>(null);

  // Monetization status
  const [isMonetizationReady, setIsMonetizationReady] = useState(false);
  const [isCheckingMonetization, setIsCheckingMonetization] = useState(true);
  const [isMonetizationExpanded, setIsMonetizationExpanded] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [nameReading, setNameReading] = useState('');
  const [nameEnglish, setNameEnglish] = useState('');
  const [category, setCategory] = useState<'original' | 'vtuber'>('original');
  const [bio, setBio] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [cardTheme, setCardTheme] = useState<'dark' | 'light'>('dark');
  const [themeColors, setThemeColors] = useState<string[]>(['#6366f1', '#ec4899', '#f59e0b']);
  const [isPublic, setIsPublic] = useState(false);
  const [allowFanart, setAllowFanart] = useState(true);
  const [fanartModerationEnabled, setFanartModerationEnabled] = useState(false);

  // 初回オンボーディングヒント
  const [showSettingsHint, setShowSettingsHint] = useState(false);

  // Refs for file inputs
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Fetch character data
  const fetchCharacter = useCallback(async () => {
    if (!characterId || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await getCharacter(characterId);
      if (data) {
        if (data.ownerId !== user.uid) {
          setError(t('errors.noPermission'));
          return;
        }

        setCharacter(data);
        setName(data.name || '');
        setNameReading(data.nameReading || '');
        setNameEnglish(data.nameEnglish || '');
        setCategory(data.category || 'original');
        setBio(data.bio || '');
        setSelectedTags(data.tags || []);
        setCardTheme(data.cardTheme || 'dark');
        setThemeColors(data.themeColors || ['#6366f1', '#ec4899', '#f59e0b']);
        setIsPublic(data.isPublic || false);
        setAllowFanart(data.allowFanart !== false);
        setFanartModerationEnabled(data.fanartModerationEnabled || false);

        // Sale settings
        setForSale(data.forSale || false);
        setSalePrice(data.salePrice || 5000);

        const hasXorPix = data.noise1Url && data.noise2Url && data.rid;
        setImagePreview(hasXorPix ? null : (data.thumbnailUrl || data.avatarUrl || data.headerImageUrl || null));
      } else {
        setError(t('errors.notFound'));
      }
    } catch (err) {
      console.error('Error fetching character:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [characterId, user]);

  // Load character when modal opens
  useEffect(() => {
    if (isOpen && characterId && user) {
      fetchCharacter();
    }
  }, [isOpen, characterId, user, fetchCharacter]);

  // 初回オンボーディングヒントチェック（Settings枠）
  useEffect(() => {
    if (isOpen) {
      const hint = sessionStorage.getItem('showSettingsHint');
      if (hint) {
        sessionStorage.removeItem('showSettingsHint');
        setTimeout(() => setShowSettingsHint(true), 500);
      }
    } else {
      setShowSettingsHint(false);
    }
  }, [isOpen]);

  // Check monetization status when modal opens
  useEffect(() => {
    const checkMonetizationStatus = async () => {
      if (!isOpen || !user) {
        setIsCheckingMonetization(false);
        return;
      }

      setIsCheckingMonetization(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // 収益化が有効: sellerApprovedかつStripe Connectが設定済み
          const hasMonetization = userData.sellerApproved === true && !!userData.stripeConnectAccountId;
          setIsMonetizationReady(hasMonetization);
        } else {
          setIsMonetizationReady(false);
        }
      } catch (err) {
        console.error('Failed to check monetization status:', err);
        setIsMonetizationReady(false);
      } finally {
        setIsCheckingMonetization(false);
      }
    };

    checkMonetizationStatus();
  }, [isOpen, user]);

  // モーダルが開いたときにbodyのスクロールを無効化
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleTag = (tag: string) => {
    // 同一カテゴリ内は排他（各カテゴリ1つまで）
    const category = getTagCategory(tag);
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      }
      // 同カテゴリの既存タグを除去してから追加
      const filtered = category
        ? prev.filter(t => getTagCategory(t) !== category)
        : prev;
      return [...filtered, tag];
    });
  };

  const updateThemeColor = (index: number, color: string) => {
    setThemeColors(prev => {
      const newColors = [...prev];
      newColors[index] = color;
      return newColors;
    });
  };

  const handleSubmit = async () => {
    if (!characterId || !user || !name.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Update character data
      await updateCharacter(characterId, {
        name: name.trim(),
        nameReading: nameReading.trim() || undefined,
        nameEnglish: nameEnglish.trim() || undefined,
        category,
        bio: bio.trim(),
        tags: selectedTags,
        cardTheme,
        themeColors,
        isPublic,
        allowFanart,
        fanartModerationEnabled: allowFanart ? fanartModerationEnabled : false,
      });

      // Update avatar if a new image was selected
      if (imageFile) {
        await updateCharacterAvatar(characterId, imageFile);
      }

      // フィードキャッシュをクリア（公開状態変更などを反映するため）
      const cacheKeys = Object.keys(localStorage).filter(key => key.startsWith('oc_feed_'));
      cacheKeys.forEach(key => localStorage.removeItem(key));

      onSaved?.();
      handleClose();
      setTimeout(() => showToast(t('toast.editComplete', 'プロフィールの編集が完了しました。'), 2500), 300);
    } catch (err) {
      console.error('Error updating character:', err);
      const errorMessage = err instanceof Error ? err.message : t('errors.saveFailed');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setNameReading('');
    setNameEnglish('');
    setCategory('original');
    setBio('');
    setSelectedTags([]);
    setImagePreview(null);
    setImageFile(null);
    setThemeColors(['#6366f1', '#ec4899', '#f59e0b']);
    setError(null);
    setCharacter(null);
    onClose();
  };

  // Get display name and username from user
  const displayName = user?.displayName || 'Creator';
  const username = user?.email?.split('@')[0] || 'username';

  // ボタンが有効かどうか
  const canSubmit = name.trim() && !isSubmitting;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景のオーバーレイ */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
          />

          {/* モーダル本体 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 py-8 sm:py-4 overflow-y-auto pointer-events-none"
          >
            <motion.div
              className="my-auto pointer-events-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 見出しと閉じるボタン */}
              <div className="mb-4 flex justify-between items-center mx-auto">
                <span className="text-xl font-bold text-white">{t('edit.title')}</span>
                <button
                  onClick={handleClose}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
              ) : error && !character ? (
                <div className="flex items-center justify-center py-20">
                  <p className="text-red-400">{error}</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-center lg:items-start xl:items-stretch">
                    {/* Card Preview + Theme */}
                    <div className="flex flex-col items-center">
                      <div className="bg-white p-2 rounded-2xl shadow-2xl">
                        <div
                          className="relative w-72 aspect-[2/3] rounded-xl overflow-hidden cursor-pointer group"
                          onClick={() => imageInputRef.current?.click()}
                        >
                          {/* Full Bleed Image */}
                          {imageFile && imagePreview ? (
                            <img
                              src={imagePreview}
                              alt="Character"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : character?.noise1Url && character?.noise2Url && character?.rid ? (
                            <XorPixViewer
                              noise1Url={character.noise1Url}
                              noise2Url={character.noise2Url}
                              rid={character.rid}
                              width={character.imageWidth || 800}
                              height={character.imageHeight || 1200}
                              characterId={character.id}
                              fallbackNoise1Url={character.fallbackNoise1Url}
                              fallbackNoise2Url={character.fallbackNoise2Url}
                              fitMode="cover"
                              className="absolute inset-0 w-full h-full"
                            />
                          ) : imagePreview ? (
                            <img
                              src={imagePreview}
                              alt="Character"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
                              <PhotoIcon className="w-12 h-12 text-gray-300 group-hover:text-gray-400 transition-colors" />
                              <span className="text-gray-400 text-sm mt-2">{t('edit.imagePlaceholder')}</span>
                            </div>
                          )}

                          {/* Top Overlay - Logo */}
                          <div className={`absolute top-0 left-0 right-0 pt-2.5 pb-8 px-4 bg-gradient-to-b ${
                            cardTheme === 'dark' ? 'from-black/40 to-transparent' : 'from-white/60 to-transparent'
                          }`}>
                            <span className={`text-[10px] font-medium tracking-[0.2em] uppercase ${
                              cardTheme === 'dark' ? 'text-white/80' : 'text-black/70'
                            }`}>
                              ORIFOLI
                            </span>
                          </div>

                          {/* English Name - Top Right, Vertical (bottom to top) */}
                          {nameEnglish && (
                            <div
                              className="absolute top-4 right-1 z-10"
                              style={{
                                writingMode: 'vertical-rl',
                                transform: 'rotate(180deg)',
                              }}
                            >
                              <span className={`text-[10px] font-light tracking-wider whitespace-nowrap ${
                                cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
                              }`}>
                                {nameEnglish}
                              </span>
                            </div>
                          )}

                          {/* Bottom Overlay - Character Info */}
                          <div className={`absolute bottom-0 left-0 right-0 pt-20 pb-3 px-4 bg-gradient-to-t ${
                            cardTheme === 'dark' ? 'from-black/60 via-black/40 to-transparent' : 'from-white/90 via-white/60 to-transparent'
                          }`}>
                            <p className={`text-xl font-semibold tracking-tight line-clamp-2 ${
                              cardTheme === 'dark' ? 'text-white' : 'text-black/60'
                            }`} style={{ lineHeight: '1.2' }}>
                              {name || <span className={cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'}>Character Name</span>}
                            </p>

                            {/* Name Reading (よみがな) */}
                            {nameReading && (
                              <p className={`text-[9px] mt-0.5 ${
                                cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
                              }`}>
                                {nameReading}
                              </p>
                            )}

                            {bio && (
                              <p className={`text-[10px] mt-1 line-clamp-2 leading-relaxed ${
                                cardTheme === 'dark' ? 'text-white/70' : 'text-gray-600'
                              }`}>
                                {bio}
                              </p>
                            )}

                            <div className="flex gap-1 mt-0.5 min-h-[14px]">
                              {selectedTags.slice(0, 3).map(tag => (
                                <span
                                  key={tag}
                                  className={`text-[9px] ${cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>

                            <div className={`border-t my-3 ${cardTheme === 'dark' ? 'border-white/20' : 'border-gray-300'}`} />

                            <div className="flex items-center gap-2">
                              <div className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 ${
                                cardTheme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
                              }`}>
                                {user?.photoURL ? (
                                  <img src={user.photoURL} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className={`w-full h-full bg-gradient-to-br ${
                                    cardTheme === 'dark' ? 'from-white/30 to-white/10' : 'from-gray-300 to-gray-200'
                                  }`} />
                                )}
                              </div>
                              <p className={`text-[9px] tracking-wide truncate ${
                                cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'
                              }`}>
                                <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>CREATOR/</span>{displayName} <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>@{username}</span>
                              </p>
                            </div>
                          </div>

                          <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                          />
                        </div>
                      </div>

                      {/* Theme Selection */}
                      <div className="w-72 mt-3 flex gap-2">
                        <button
                          onClick={() => setCardTheme('dark')}
                          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                            cardTheme === 'dark'
                              ? 'bg-gray-900 text-white'
                              : 'bg-white/10 text-white/60 hover:bg-white/20'
                          }`}
                        >
                          {t('edit.theme.dark')}
                        </button>
                        <button
                          onClick={() => setCardTheme('light')}
                          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                            cardTheme === 'light'
                              ? 'bg-white text-gray-900'
                              : 'bg-white/10 text-white/60 hover:bg-white/20'
                          }`}
                        >
                          {t('edit.theme.light')}
                        </button>
                      </div>

                      {/* appeear.me AR連携 */}
                      {characterId && (
                        <div className="w-72 mt-3">
                          <ARCreateSection
                            character={character}
                            onARLinked={async (arModelId) => {
                              // Update character with AR model ID
                              try {
                                await updateCharacterService(characterId, {
                                  arModelId,
                                  arModelStatus: 'active',
                                } as Partial<Character>);
                                // Update local state
                                if (character) {
                                  setCharacter({
                                    ...character,
                                    arModelId,
                                    arModelStatus: 'active',
                                  });
                                }
                              } catch (err) {
                                console.error('Failed to save AR model ID:', err);
                              }
                            }}
                            onARUnlinked={() => {
                              // Update local state when AR is unlinked
                              if (character) {
                                setCharacter({
                                  ...character,
                                  arModelId: undefined,
                                  arModelStatus: undefined,
                                });
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Controls Panel - Basic Info */}
                    <div className="w-72 lg:w-64 space-y-3">
                      {/* Category Selection */}
                      <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4">
                        <h3 className="text-xs font-medium text-white tracking-wide uppercase mb-2">{t('edit.category.label', 'CATEGORY')}</h3>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setCategory('original')}
                            className={`flex-1 px-3 py-2 text-xs rounded-lg transition-colors ${
                              category === 'original'
                                ? 'bg-purple-500 text-white'
                                : 'bg-white/20 text-white/70 hover:bg-white/30'
                            }`}
                          >
                            {t('edit.category.original', 'オリキャラ')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setCategory('vtuber')}
                            className={`flex-1 px-3 py-2 text-xs rounded-lg transition-colors ${
                              category === 'vtuber'
                                ? 'bg-purple-500 text-white'
                                : 'bg-white/20 text-white/70 hover:bg-white/30'
                            }`}
                          >
                            {t('edit.category.vtuber', 'Vtuber')}
                          </button>
                        </div>
                      </div>

                      {/* Name Input */}
                      <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-xs font-medium text-white tracking-wide uppercase">{t('edit.name.label')}</h3>
                          <span className="text-[10px] text-white/40">{name.length}/30</span>
                        </div>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder={t('edit.name.placeholder')}
                          className="w-full bg-white/90 text-black text-sm rounded-lg px-3 py-2 placeholder-gray-500 outline-none focus:bg-white transition-colors"
                          maxLength={30}
                        />

                        {/* Name Reading (よみがな) */}
                        <div className="mt-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[10px] text-white/60">{t('edit.name.reading', 'よみがな')}</span>
                            <span className="text-[10px] text-white/40">{nameReading.length}/30</span>
                          </div>
                          <input
                            type="text"
                            value={nameReading}
                            onChange={(e) => setNameReading(e.target.value)}
                            placeholder={t('edit.name.readingPlaceholder', 'ひらがな/カタカナ')}
                            className="w-full bg-white/80 text-black text-xs rounded-lg px-3 py-1.5 placeholder-gray-400 outline-none focus:bg-white transition-colors"
                            maxLength={30}
                          />
                        </div>

                        {/* Name English (英語表記) */}
                        <div className="mt-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[10px] text-white/60">{t('edit.name.english', '英語表記')}</span>
                            <span className="text-[10px] text-white/40">{nameEnglish.length}/50</span>
                          </div>
                          <input
                            type="text"
                            value={nameEnglish}
                            onChange={(e) => setNameEnglish(e.target.value)}
                            placeholder={t('edit.name.englishPlaceholder', 'English Name')}
                            className="w-full bg-white/80 text-black text-xs rounded-lg px-3 py-1.5 placeholder-gray-400 outline-none focus:bg-white transition-colors"
                            maxLength={50}
                          />
                        </div>
                      </div>

                      {/* Bio Input */}
                      <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-xs font-medium text-white tracking-wide uppercase">{t('edit.bio.label')}</h3>
                          <span className="text-[10px] text-white/40">{bio.length}/100</span>
                        </div>
                        <textarea
                          value={bio}
                          onChange={(e) => setBio(e.target.value)}
                          placeholder={t('edit.bio.placeholder')}
                          className="w-full bg-white/90 text-black text-xs rounded-lg p-2 placeholder-gray-500 outline-none focus:bg-white transition-colors resize-y"
                          rows={3}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Tags Panel - 3rd column on xl */}
                    <div className="w-72 lg:w-64 xl:w-56 space-y-3">
                      {/* Tag Selection */}
                      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
                        <h3 className="text-xs font-medium text-white/60 mb-3 tracking-wide uppercase">{t('edit.tags.label')}</h3>

                        <div className="mb-3">
                          <p className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">{t('edit.tags.genre')}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {CHARACTER_TAGS.genre.options.map(option => (
                              <button
                                key={option.value}
                                onClick={() => toggleTag(option.value)}
                                className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                                  selectedTags.includes(option.value)
                                    ? 'bg-white text-gray-900'
                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                                }`}
                              >
                                {tTop(`tags.genre.${option.value}`, { defaultValue: option.label })}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="mb-3">
                          <p className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">{t('edit.tags.personality')}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {CHARACTER_TAGS.personality.options.map(option => (
                              <button
                                key={option.value}
                                onClick={() => toggleTag(option.value)}
                                className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                                  selectedTags.includes(option.value)
                                    ? 'bg-white text-gray-900'
                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                                }`}
                              >
                                {tTop(`tags.personality.${option.value}`, { defaultValue: option.label })}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <p className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">{t('edit.tags.worldview')}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {CHARACTER_TAGS.worldview.options.map(option => (
                              <button
                                key={option.value}
                                onClick={() => toggleTag(option.value)}
                                className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                                  selectedTags.includes(option.value)
                                    ? 'bg-white text-gray-900'
                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                                }`}
                              >
                                {tTop(`tags.worldview.${option.value}`, { defaultValue: option.label })}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Theme Colors */}
                      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
                        <h3 className="text-xs font-medium text-white/60 mb-2 tracking-wide uppercase">{t('edit.themeColors.label')}</h3>
                        <p className="text-[10px] text-white/40 mb-3">{t('edit.themeColors.description')}</p>
                        <div className="flex gap-3 justify-center">
                          {[0, 1, 2].map((index) => (
                            <div key={index} className="flex flex-col items-center gap-1">
                              <label className="relative cursor-pointer group">
                                <div
                                  className="w-10 h-10 rounded-lg shadow-lg transition-transform group-hover:scale-105 border-2 border-white/20"
                                  style={{ backgroundColor: themeColors[index] || '#888888' }}
                                />
                                <input
                                  type="color"
                                  value={themeColors[index] || '#888888'}
                                  onChange={(e) => updateThemeColor(index, e.target.value)}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                              </label>
                              <span className="text-[8px] text-white/30">
                                {index === 0 ? t('edit.themeColors.primary') : index === 1 ? t('edit.themeColors.secondary') : t('edit.themeColors.accent')}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 h-1.5 rounded-full overflow-hidden flex">
                          <div className="flex-1" style={{ backgroundColor: themeColors[0] }} />
                          <div className="flex-1" style={{ backgroundColor: themeColors[1] }} />
                          <div className="flex-1" style={{ backgroundColor: themeColors[2] }} />
                        </div>
                      </div>
                    </div>

                    {/* Controls Panel - Settings & Sale */}
                    <div className="w-72 lg:w-64 space-y-3">
                      {/* Settings */}
                      <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-4 space-y-3">
                        {/* オンボーディングヒント */}
                        {showSettingsHint && (
                          <div
                            className="absolute -top-2 left-4 -translate-y-full z-[60]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="relative bg-white text-gray-900 text-xs leading-relaxed rounded-xl px-4 py-3 pr-7 shadow-lg w-[200px]">
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowSettingsHint(false); }}
                                className="absolute top-1.5 right-1.5 p-0.5 text-gray-400 hover:text-gray-700 transition-colors"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                              <p>{t('edit.hints.settingsPublish', 'ここから公開設定やファンアート許可をします')}</p>
                            </div>
                            <div className="flex justify-start pl-4">
                              <svg width="14" height="8" viewBox="0 0 14 8" className="text-white">
                                <polygon points="0,0 14,0 7,8" fill="currentColor" />
                              </svg>
                            </div>
                          </div>
                        )}
                        <h3 className="text-xs font-medium text-white/60 mb-2 tracking-wide uppercase">{t('edit.settingsSection.label')}</h3>

                        <label className="flex items-center justify-between cursor-pointer">
                          <div>
                            <p className="text-xs font-medium text-white">{t('edit.settingsSection.public.title')}</p>
                            <p className="text-[9px] text-white/40">{t('edit.settingsSection.public.description')}</p>
                          </div>
                          <div className={`w-9 h-5 rounded-full p-0.5 transition-colors ${isPublic ? 'bg-green-500' : 'bg-white/20'}`}>
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${isPublic ? 'translate-x-4' : ''}`} />
                          </div>
                          <input
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                            className="hidden"
                          />
                        </label>

                        <label className="flex items-center justify-between cursor-pointer">
                          <div>
                            <p className="text-xs font-medium text-white">{t('edit.settingsSection.fanart.title')}</p>
                            <p className="text-[9px] text-white/40">{t('edit.settingsSection.fanart.description')}</p>
                          </div>
                          <div className={`w-9 h-5 rounded-full p-0.5 transition-colors ${allowFanart ? 'bg-green-500' : 'bg-white/20'}`}>
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${allowFanart ? 'translate-x-4' : ''}`} />
                          </div>
                          <input
                            type="checkbox"
                            checked={allowFanart}
                            onChange={(e) => setAllowFanart(e.target.checked)}
                            className="hidden"
                          />
                        </label>

                        {/* ファンアートモデレーション設定（ファンアート許可時のみ表示） */}
                        {allowFanart && (
                          <label className="flex items-center justify-between cursor-pointer pl-4 border-l-2 border-white/10">
                            <div>
                              <p className="text-xs font-medium text-white">{t('edit.settingsSection.fanartModeration.title')}</p>
                              <p className="text-[9px] text-white/40">{t('edit.settingsSection.fanartModeration.description')}</p>
                            </div>
                            <div className={`w-9 h-5 rounded-full p-0.5 transition-colors ${fanartModerationEnabled ? 'bg-yellow-500' : 'bg-white/20'}`}>
                              <div className={`w-4 h-4 rounded-full bg-white transition-transform ${fanartModerationEnabled ? 'translate-x-4' : ''}`} />
                            </div>
                            <input
                              type="checkbox"
                              checked={fanartModerationEnabled}
                              onChange={(e) => setFanartModerationEnabled(e.target.checked)}
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>

                      {/* 収益化設定セクション（おむかえ + ライセンス） */}
                      <div className="bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden">
                        {/* 見出し（トグル） */}
                        <button
                          onClick={() => setIsMonetizationExpanded(!isMonetizationExpanded)}
                          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <CurrencyYenIcon className="w-4 h-4 text-white/60" />
                            <h3 className="text-xs font-medium text-white/80 tracking-wide">{t('edit.monetization.label')}</h3>
                            {!isMonetizationReady && !isCheckingMonetization && (
                              <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-[9px] rounded">{t('edit.monetization.required')}</span>
                            )}
                          </div>
                          <svg
                            className={`w-4 h-4 text-white/40 transition-transform ${isMonetizationExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* 展開コンテンツ */}
                        {isMonetizationExpanded && (
                          <div className="px-4 pb-4 space-y-3">
                            {isCheckingMonetization ? (
                              // 確認中
                              <div className="flex items-center justify-center gap-2 py-4">
                                <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs text-white/40">{t('edit.monetization.checking')}</span>
                              </div>
                            ) : !isMonetizationReady ? (
                              // 収益化未設定 - 機能の説明 + 申請導線
                              <>
                                {/* 収益化が必要メッセージ */}
                                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-3">
                                  <div className="flex gap-2">
                                    <ExclamationTriangleIcon className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-[10px] text-yellow-300/80 leading-relaxed">
                                        {t('edit.monetization.warningMessage')}
                                      </p>
                                      <a
                                        href={`${config.figryneUrl}/creator/onboarding`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block mt-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-medium rounded-lg transition-colors"
                                      >
                                        {t('edit.monetization.applyButton')}
                                      </a>
                                    </div>
                                  </div>
                                </div>

                                {/* おむかえ設定（無効表示） */}
                                <div className="opacity-70 pointer-events-none">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="text-[11px] font-medium text-white/80 uppercase">{t('edit.monetization.sale.label')}</h4>
                                  </div>
                                  <p className="text-[10px] text-white/70">
                                    {t('edit.monetization.sale.description')}
                                  </p>
                                </div>

                                <div className="border-t border-white/10" />

                                {/* ライセンス設定（無効表示） */}
                                <div className="opacity-70 pointer-events-none">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="text-[11px] font-medium text-white/80 uppercase">{t('edit.monetization.licensing.label')}</h4>
                                  </div>
                                  <p className="text-[10px] text-white/70">
                                    {t('edit.monetization.licensing.description')}
                                  </p>
                                </div>
                              </>
                            ) : (
                              // 収益化設定済み - 実際の設定を表示
                              <>
                                {/* おむかえ設定 (Sale) */}
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="text-[10px] font-medium text-white/60 uppercase">{t('edit.monetization.sale.label')}</h4>
                                  </div>
                                  <p className="text-[9px] text-white/40 mb-3">
                                    {t('edit.monetization.sale.enabledDescription')}
                                    <span className="text-white/30">{t('edit.monetization.sale.authorNote')}</span>
                                  </p>

                                  {/* Toggle */}
                                  <label className="flex items-center justify-between cursor-pointer mb-3">
                                    <span className="text-xs font-medium text-white">{t('edit.monetization.sale.toggle')}</span>
                                    <div
                                      className={`w-9 h-5 rounded-full p-0.5 transition-colors ${
                                        forSale ? 'bg-green-500' : 'bg-white/20'
                                      }`}
                                      onClick={() => {
                                        setForSale(!forSale);
                                        setSaleSaveSuccess(false);
                                      }}
                                    >
                                      <div
                                        className={`w-4 h-4 rounded-full bg-white transition-transform ${
                                          forSale ? 'translate-x-4' : ''
                                        }`}
                                      />
                                    </div>
                                  </label>

                                  {forSale && (
                                    <div className="space-y-3">
                                      {/* Price Input */}
                                      <div>
                                        <label className="text-xs text-white/60 mb-1 block">{t('edit.monetization.sale.priceLabel')}</label>
                                        <div className="relative">
                                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">¥</span>
                                          <input
                                            type="number"
                                            min={1000}
                                            max={1000000}
                                            step={100}
                                            value={salePrice}
                                            onChange={(e) => {
                                              const value = parseInt(e.target.value) || 0;
                                              setSalePrice(value);
                                              const validation = validateSalePrice(value);
                                              setSalePriceError(validation.error || null);
                                              setSaleSaveSuccess(false);
                                            }}
                                            className="w-full pl-8 pr-3 py-2 bg-white/10 text-white text-sm rounded-lg outline-none focus:bg-white/15 transition-colors"
                                          />
                                        </div>
                                        {salePriceError && (
                                          <p className="text-[10px] text-red-400 mt-1">{salePriceError}</p>
                                        )}
                                        <p className="text-[9px] text-white/30 mt-1">
                                          {t('edit.monetization.sale.priceRange')}
                                        </p>
                                      </div>

                                      {/* Save Button */}
                                      <button
                                        onClick={async () => {
                                          if (!character) return;
                                          const validation = validateSalePrice(salePrice);
                                          if (!validation.valid) {
                                            setSalePriceError(validation.error || null);
                                            return;
                                          }
                                          setIsSavingSale(true);
                                          setSaleSaveSuccess(false);
                                          try {
                                            await updateSaleSettings(character.id, {
                                              forSale: true,
                                              salePrice,
                                            });
                                            setSaleSaveSuccess(true);
                                            setTimeout(() => setSaleSaveSuccess(false), 3000);
                                          } catch (err) {
                                            console.error('Failed to save sale settings:', err);
                                            setSalePriceError('保存に失敗しました');
                                          } finally {
                                            setIsSavingSale(false);
                                          }
                                        }}
                                        disabled={isSavingSale || !!salePriceError}
                                        className="w-full py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                      >
                                        {isSavingSale ? (
                                          <>
                                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            {t('edit.monetization.sale.saving')}
                                          </>
                                        ) : saleSaveSuccess ? (
                                          <>
                                            <CheckCircleIcon className="w-4 h-4 text-green-400" />
                                            {t('edit.monetization.sale.saved')}
                                          </>
                                        ) : (
                                          t('edit.monetization.sale.startSale', { price: formatPrice(salePrice) })
                                        )}
                                      </button>
                                    </div>
                                  )}

                                  {!forSale && character?.forSale && (
                                    <button
                                      onClick={async () => {
                                        if (!character) return;
                                        setIsSavingSale(true);
                                        try {
                                          await updateSaleSettings(character.id, {
                                            forSale: false,
                                          });
                                          setCharacter({ ...character, forSale: false });
                                        } catch (err) {
                                          console.error('Failed to stop sale:', err);
                                        } finally {
                                          setIsSavingSale(false);
                                        }
                                      }}
                                      disabled={isSavingSale}
                                      className="w-full py-2 bg-white/10 hover:bg-white/20 text-white/60 text-xs font-medium rounded-lg transition-colors"
                                    >
                                      {isSavingSale ? t('edit.monetization.sale.processing') : t('edit.monetization.sale.stopSale')}
                                    </button>
                                  )}

                                  {character?.transferCount && character.transferCount > 0 && (
                                    <p className="text-[9px] text-white/30 text-center mt-2">
                                      {t('edit.monetization.sale.transferHistory', { count: character.transferCount })}
                                    </p>
                                  )}
                                </div>

                                <div className="border-t border-white/10" />

                                {/* ライセンス設定 (Licensing) */}
                                <LicensingSettingsPanel
                                  character={character}
                                  onLicensingUpdated={(licensing: CharacterLicensing) => {
                                    // Update local character state
                                    if (character) {
                                      setCharacter({
                                        ...character,
                                        licensing,
                                      });
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mt-4 flex justify-center">
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className="bg-white text-gray-900 px-12 py-3 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-900"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('edit.submit.saving')}
                        </span>
                      ) : (
                        t('edit.submit.button')
                      )}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        </>
      )}

    </AnimatePresence>
  );
};

export default CharacterEditModal;
